import { useState } from 'react';
import {
  services,
  howItWorks,
  alterationTestimonials,
  trustBadges,
} from '../../../mocks/pretAPorter';
import { useSupabaseAdmin } from '../../../hooks/useSupabaseAdmin';
import ServicesTab from './services/ServicesTab';
import StepsTab from './services/StepsTab';
import TestimonialsTab from './services/TestimonialsTab';
import BadgesTab from './services/BadgesTab';

interface ServiceItem {
  name: string;
  price: string;
  duration: string;
}

interface Service {
  id: number;
  category: string;
  items: ServiceItem[];
  icon: string;
}

interface Step {
  id: number;
  step: number;
  title: string;
  description: string;
  icon: string;
}

interface Testimonial {
  id: number;
  name: string;
  location: string;
  text: string;
  rating: number;
}

interface Badge {
  id: number;
  icon: string;
  label: string;
  description: string;
}

function serviceToDb(item: Partial<Service>): Record<string, unknown> {
  return {
    category: item.category,
    icon: item.icon,
    items: item.items ?? [],
  };
}

function serviceFromDb(row: Record<string, unknown>): Service {
  return {
    id: row.id as number,
    category: row.category as string,
    icon: row.icon as string,
    items: (row.items as ServiceItem[]) ?? [],
  };
}

type Tab = 'services' | 'steps' | 'testimonials' | 'badges';

export default function AdminServices() {
  const [activeTab, setActiveTab] = useState<Tab>('services');

  const servicesData = useSupabaseAdmin<Service>({
    table: 'services',
    initialData: services,
    mapToDb: serviceToDb,
    mapFromDb: serviceFromDb,
  });

  const stepsData = useSupabaseAdmin<Step>({
    table: 'how_it_works_steps',
    initialData: howItWorks.map((s) => ({ ...s, id: s.step })),
  });

  const testimonialsData = useSupabaseAdmin<Testimonial>({
    table: 'testimonials',
    initialData: alterationTestimonials.map((t) => ({
      ...t,
      type: 'arreglos' as const,
      date: '2026-01-01',
      service: 'Arreglo',
    })),
  });

  const badgesData = useSupabaseAdmin<Badge>({
    table: 'trust_badges',
    initialData: trustBadges.map((b, i) => ({ ...b, id: i + 1 })),
  });

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'services', label: 'Servicios', count: servicesData.data.length },
    { key: 'steps', label: 'Proceso', count: stepsData.data.length },
    {
      key: 'testimonials',
      label: 'Testimonios',
      count: testimonialsData.data.length,
    },
    { key: 'badges', label: 'Badges', count: badgesData.data.length },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-full font-sans text-sm whitespace-nowrap transition-colors ${
              activeTab === tab.key
                ? 'bg-brand-black text-white'
                : 'bg-white border border-brand-gray-200 text-brand-gray-600 hover:text-brand-black hover:border-brand-gray-400'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {activeTab === 'services' && (
        <ServicesTab
          data={servicesData.data}
          add={servicesData.add}
          update={servicesData.update}
          remove={servicesData.remove}
        />
      )}
      {activeTab === 'steps' && (
        <StepsTab
          data={stepsData.data}
          add={stepsData.add}
          update={stepsData.update}
          remove={stepsData.remove}
        />
      )}
      {activeTab === 'testimonials' && (
        <TestimonialsTab
          data={testimonialsData.data}
          add={testimonialsData.add}
          update={testimonialsData.update}
          remove={testimonialsData.remove}
        />
      )}
      {activeTab === 'badges' && (
        <BadgesTab
          data={badgesData.data}
          add={badgesData.add}
          update={badgesData.update}
          remove={badgesData.remove}
        />
      )}
    </div>
  );
}
